import { useEffect, useRef, useState } from 'react';

export const useIsElementSticky = (
  ref: React.RefObject<HTMLElement | null>,
): boolean => {
  const [isSticky, setIsSticky] = useState(false);
  const rafRef = useRef<number>();
  const prevStuckRef = useRef<boolean | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    prevStuckRef.current = null;

    const scheduleUpdate = () => {
      if (rafRef.current !== undefined) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = undefined;
        const stuck = checkIsStuck(element);

        if (prevStuckRef.current !== stuck) {
          prevStuckRef.current = stuck;
          setIsSticky(stuck);
        }
      });
    };

    scheduleUpdate();

    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate, { passive: true });

    return () => {
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);

      if (rafRef.current !== undefined) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = undefined;
      }
    };
  }, [ref]);

  return isSticky;
};

const checkIsStuck = (element: HTMLElement): boolean => {
  const { top } = element.getBoundingClientRect();
  const stickyTop = parseFloat(getComputedStyle(element).top) || 0;
  return top <= stickyTop;
};
